import type { OnboardingOption } from 'features/onboarding/api/options';

type CityLang = 'en' | 'ar' | 'he';

/** Normalize an i18next language code to one of en/ar/he. */
const normalizeLang = (lng: string | undefined): CityLang => {
  const base = (lng || 'he').slice(0, 2).toLowerCase();
  if (base === 'ar') return 'ar';
  if (base === 'en') return 'en';
  return 'he';
};

/**
 * Resolve the display label for a city (area) option. The haat-hub Partner
 * Connect Management app is the single source of truth: show the localized
 * override for the active language when set, otherwise fall back to the hub's
 * English name and finally the cleaned Pipedrive English label.
 */
export const localizedCityLabel = (
  option: OnboardingOption,
  language: string | undefined,
): string => {
  const lang = normalizeLang(language);
  const localized =
    lang === 'ar' ? option.nameAr : lang === 'en' ? option.nameEn : option.nameHe;

  if (localized && localized.trim()) return localized.trim();

  const english = option.nameEn?.trim();
  if (english) return english;

  return option.labelClean;
};

const normalizeDedupKey = (label: string): string =>
  label
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[\s\-_/(),.]+/g, '')
    .trim();

/**
 * Map onboarding area options to `{ value, label }` pairs for the city picker.
 * Dedupes on both the display label and the English label, so the same city
 * arriving as two Pipedrive entries (or one localized and one not) collapses
 * into a single row (fixes duplicate Tel Aviv entries).
 */
export const buildCityOptions = (
  areas: OnboardingOption[] | undefined,
  language: string | undefined,
): Array<{ value: string; label: string }> => {
  const seen = new Set<string>();
  const result: Array<{ value: string; label: string }> = [];

  for (const o of areas ?? []) {
    const label = localizedCityLabel(o, language);
    const keys = [
      normalizeDedupKey(label),
      normalizeDedupKey(o.labelClean),
      normalizeDedupKey(o.nameEn ?? ''),
    ].filter(Boolean);
    if (keys.length === 0 || keys.some((k) => seen.has(k))) continue;
    keys.forEach((k) => seen.add(k));
    result.push({
      value: o.label,
      label,
    });
  }

  return result;
};

/**
 * Map onboarding store-type options to `{ value, label }` pairs for the store
 * type picker. Reuses the same localized-label resolution as cities.
 */
export const buildStoreTypeOptions = (
  storeTypes: OnboardingOption[] | undefined,
  language: string | undefined,
): Array<{ value: string; label: string }> =>
  (storeTypes ?? []).map((o) => ({
    value: o.labelClean,
    label: localizedCityLabel(o, language),
  }));

const isOtherOption = (o: OnboardingOption): boolean =>
  o.labelClean.trim().toLowerCase() === 'other' ||
  o.labelClean.trim() === 'אחר' ||
  o.nameHe?.trim() === 'אחר';

/**
 * Keep only the store-type options tagged with the given business category
 * (Restaurant / Market). The haat-hub Partner Connect Management app is the
 * single source of truth for the list itself; when no option carries the tag
 * yet, every visible store type is shown so the picker never turns up empty.
 * "Other" is appended when the hub list does not already provide it, since it
 * drives the free-text input rather than a managed value.
 */
export const filterStoreTypesByCategory = (
  storeTypes: OnboardingOption[] | undefined,
  category: 'restaurant' | 'market' | '',
): OnboardingOption[] => {
  if (!category) return [];

  const all = storeTypes ?? [];
  const tagged = all.filter((o) => o.category === category);
  const visible = tagged.length > 0 ? tagged : all;

  if (visible.some(isOtherOption)) return visible;

  return [
    ...visible,
    {
      id: -1,
      label: 'Other',
      labelClean: 'Other',
      nameEn: 'Other',
      nameHe: 'אחר',
      nameAr: 'أخرى',
      category,
    },
  ];
};
